/**
 * useSkeletonState - Hook for managing skeleton loading states
 *
 * Prevents skeleton flash on fast loads by enforcing minimum display time.
 * Handles the transition from skeleton to content smoothly.
 *
 * @module hooks/useSkeletonState
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * @typedef {Object} SkeletonStateOptions
 * @property {boolean} isLoading - External loading state
 * @property {number} [minDisplayMs=300] - Minimum time to show skeleton
 * @property {number} [delayMs=0] - Delay before showing skeleton (prevents flash on fast loads)
 */

/**
 * @typedef {Object} SkeletonStateReturn
 * @property {boolean} showSkeleton - Whether to render skeleton
 * @property {boolean} showContent - Whether to render actual content
 * @property {boolean} isTransitioning - Whether transitioning from skeleton to content
 * @property {Function} contentReady - Call when content is ready to display
 */

/**
 * Hook for managing skeleton display state
 *
 * @param {SkeletonStateOptions} options
 * @returns {SkeletonStateReturn}
 *
 * @example
 * const { showSkeleton, showContent } = useSkeletonState({
 *   isLoading: isFetching,
 *   minDisplayMs: 300,
 * });
 *
 * return showSkeleton ? <ProjectSkeleton /> : <ProjectCard data={data} />;
 */
export function useSkeletonState({
    isLoading,
    minDisplayMs = 300,
    delayMs = 0,
}) {
    const [showSkeleton, setShowSkeleton] = useState(isLoading);
    const [showContent, setShowContent] = useState(!isLoading);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const skeletonStartRef = useRef(null);
    const delayTimeoutRef = useRef(null);
    const minTimeoutRef = useRef(null);

    useEffect(() => {
        // Clear any pending timeouts
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
        }
        if (minTimeoutRef.current) {
            clearTimeout(minTimeoutRef.current);
        }

        if (isLoading) {
            // Starting to load
            setShowContent(false);

            if (delayMs > 0) {
                // Delay showing skeleton (for fast loads)
                delayTimeoutRef.current = setTimeout(() => {
                    setShowSkeleton(true);
                    skeletonStartRef.current = Date.now();
                }, delayMs);
            } else {
                // Show skeleton immediately
                setShowSkeleton(true);
                skeletonStartRef.current = Date.now();
            }
        } else {
            // Loading finished
            const skeletonStart = skeletonStartRef.current;

            if (skeletonStart && minDisplayMs > 0) {
                // Ensure minimum display time
                const elapsed = Date.now() - skeletonStart;
                const remaining = Math.max(0, minDisplayMs - elapsed);

                if (remaining > 0) {
                    setIsTransitioning(true);
                    minTimeoutRef.current = setTimeout(() => {
                        setShowSkeleton(false);
                        setShowContent(true);
                        setIsTransitioning(false);
                        skeletonStartRef.current = null;
                    }, remaining);
                } else {
                    setShowSkeleton(false);
                    setShowContent(true);
                    skeletonStartRef.current = null;
                }
            } else {
                // No minimum time or skeleton wasn't shown
                setShowSkeleton(false);
                setShowContent(true);
                skeletonStartRef.current = null;
            }
        }

        return () => {
            if (delayTimeoutRef.current) {
                clearTimeout(delayTimeoutRef.current);
            }
            if (minTimeoutRef.current) {
                clearTimeout(minTimeoutRef.current);
            }
        };
    }, [isLoading, minDisplayMs, delayMs]);

    // Manual content ready trigger
    const contentReady = useCallback(() => {
        setShowSkeleton(false);
        setShowContent(true);
        setIsTransitioning(false);
    }, []);

    return {
        showSkeleton,
        showContent,
        isTransitioning,
        contentReady,
    };
}

/**
 * Hook for multiple skeleton items with staggered reveal
 *
 * @param {Object} options
 * @param {boolean} options.isLoading - Loading state
 * @param {number} options.itemCount - Number of items to show
 * @param {number} [options.staggerMs=50] - Delay between each item reveal
 */
export function useStaggeredSkeleton({
    isLoading,
    itemCount,
    staggerMs = 50,
}) {
    const [revealedCount, setRevealedCount] = useState(0);
    const intervalsRef = useRef([]);

    useEffect(() => {
        // Clear existing intervals
        intervalsRef.current.forEach(clearTimeout);
        intervalsRef.current = [];

        if (!isLoading && itemCount > 0) {
            // Stagger reveal items
            for (let i = 0; i < itemCount; i++) {
                const timeout = setTimeout(() => {
                    setRevealedCount(prev => Math.min(prev + 1, itemCount));
                }, i * staggerMs);
                intervalsRef.current.push(timeout);
            }
        } else if (isLoading) {
            setRevealedCount(0);
        }

        return () => {
            intervalsRef.current.forEach(clearTimeout);
        };
    }, [isLoading, itemCount, staggerMs]);

    return {
        revealedCount,
        isFullyRevealed: revealedCount >= itemCount,
        getItemState: (index) => ({
            isRevealed: index < revealedCount,
            isSkeleton: isLoading || index >= revealedCount,
        }),
    };
}

export default useSkeletonState;
